function compact(value) {
  if (Array.isArray(value)) return value.map(compact).filter((item) => item !== undefined);
  if (!value || typeof value !== 'object') return value === null || value === '' || value === undefined ? undefined : value;
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, compact(item)]).filter(([, item]) => item !== undefined));
}

export function buildOrganizationSchema({ origin, name, description, logo, sameAs = [] }) {
  if (!origin) return undefined;
  return compact({ '@context': 'https://schema.org', '@type': 'Organization', '@id': `${origin}/#organization`, name, url: origin, description, logo, sameAs });
}

export function buildWebSiteSchema({ origin, name }) {
  if (!origin) return undefined;
  return compact({ '@context': 'https://schema.org', '@type': 'WebSite', '@id': `${origin}/#website`, name, url: origin, publisher: { '@id': `${origin}/#organization` } });
}

export function buildWebPageSchema({ url, name, description, type = 'WebPage' }) {
  if (!url) return undefined;
  return compact({ '@context': 'https://schema.org', '@type': type, '@id': `${url}#webpage`, url, name, description, isPartOf: { '@id': `${new URL(url).origin}/#website` } });
}

export function buildBreadcrumbSchema({ url, items = [] }) {
  if (!url) return undefined;
  return compact({ '@context': 'https://schema.org', '@type': 'BreadcrumbList', '@id': `${url}#breadcrumb`, itemListElement: items.map((item, index) => ({ '@type': 'ListItem', position: index + 1, name: item.name, item: item.url })) });
}

export function buildItemListSchema({ url, name, items = [] }) {
  if (!url) return undefined;
  return compact({ '@context': 'https://schema.org', '@type': 'ItemList', '@id': `${url}#items`, name, numberOfItems: items.length, itemListElement: items.map((item, index) => ({ '@type': 'ListItem', position: index + 1, name: item.name, url: item.url })) });
}

export function buildLocalBusinessSchema(input) {
  if (!input.url) return undefined;
  return compact({
    '@context': 'https://schema.org', '@type': input.type || 'LocalBusiness', '@id': `${input.url}#business`,
    name: input.name, url: input.url, description: input.description, image: input.images,
    telephone: input.telephone, sameAs: input.sameAs,
    address: input.address ? { '@type': 'PostalAddress', streetAddress: input.address.street, addressLocality: input.address.city, addressRegion: input.address.region, postalCode: input.address.postalCode, addressCountry: input.address.country } : undefined,
    geo: input.geo ? { '@type': 'GeoCoordinates', latitude: input.geo.latitude, longitude: input.geo.longitude } : undefined,
    openingHoursSpecification: input.hours, areaServed: input.areaServed,
  });
}

export function buildArticleSchema(input) {
  if (!input.url) return undefined;
  return compact({ '@context': 'https://schema.org', '@type': 'Article', '@id': `${input.url}#article`, headline: input.headline, description: input.description, url: input.url, mainEntityOfPage: { '@id': `${input.url}#webpage` }, datePublished: input.datePublished, dateModified: input.dateModified, image: input.images, author: input.author, publisher: { '@id': `${new URL(input.url).origin}/#organization` } });
}

export function buildEventSchema(input) {
  if (!input.url) return undefined;
  return compact({ '@context': 'https://schema.org', '@type': 'Event', '@id': `${input.url}#event`, name: input.name, description: input.description, url: input.url, image: input.images, startDate: input.startDate, endDate: input.endDate, eventStatus: input.eventStatus, eventAttendanceMode: input.attendanceMode, location: input.location, organizer: input.organizer, offers: input.offers });
}

export function buildOfferSchema(input) {
  if (!input.url) return undefined;
  return compact({ '@context': 'https://schema.org', '@type': 'Offer', '@id': `${input.url}#offer`, name: input.name, description: input.description, url: input.url, image: input.image, validFrom: input.validFrom, validThrough: input.validThrough, availability: input.availability, price: input.price, priceCurrency: input.priceCurrency, seller: input.seller });
}

export function buildPersonSchema(input) {
  if (!input.url) return undefined;
  return compact({ '@context': 'https://schema.org', '@type': 'Person', '@id': `${input.url}#person`, name: input.name, url: input.url, description: input.description, image: input.image, jobTitle: input.jobTitle, worksFor: input.worksFor, knowsAbout: input.knowsAbout, sameAs: input.sameAs, hasCredential: Array.isArray(input.credentials) && input.credentials.length ? input.credentials.filter(name => typeof name === 'string' && name.trim()).map(name => ({ '@type': 'EducationalOccupationalCredential', name })) : undefined });
}

export function safeJsonLd(value) {
  return JSON.stringify(compact(value)).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
}

export { compact as compactSchema };

// Compose at serialization time: page factories may replace their entity array,
// while tenant definitions and graph relationships stay request-local and shared
// by initial HTML and subsequent browser navigation.
export function composePublicSchemaGraph(metadata) {
  if (!metadata?.canonicalUrl || /\bnoindex\b/i.test(metadata.robots || '')) return [];
  const canonical = metadata.canonicalUrl;
  let origin;
  try { const url = new URL(canonical); if (url.protocol !== 'https:' || url.username || url.password || url.port) return []; origin = url.origin; } catch { return []; }
  const site = (metadata.siteJsonLd || []).filter(Boolean);
  if (site.some(node => { try { return new URL(node.url).origin !== origin || !node['@id']?.startsWith(`${origin}/#`); } catch { return true; } })) return [];
  const siteIds = new Set(site.map(node => node['@id']).filter(Boolean));
  const nodes = new Map();
  const anonymous = [];
  const pageTypes = new Set(['WebPage', 'CollectionPage', 'ProfilePage']);
  let page;
  for (const node of [...site, ...(metadata.jsonLd || []).filter(Boolean)]) {
    if (pageTypes.has(node['@type'])) { page = { ...node }; continue; }
    if (!node['@id']) { anonymous.push({ ...node }); continue; }
    if (nodes.has(node['@id']) && siteIds.has(node['@id'])) continue;
    nodes.set(node['@id'], { ...node });
  }
  const website = site.find(node => node['@type'] === 'WebSite');
  page = { ...page, '@context': 'https://schema.org', '@type': page?.['@type'] || 'WebPage', '@id': `${canonical}#webpage`, url: canonical, name: metadata.title, description: metadata.description,
    ...(website ? { isPartOf: { '@id': website['@id'] } } : {}),
  };
  const primaryIds = new Set(['business', 'event', 'article', 'person', 'offer', 'items'].map(fragment => `${canonical}#${fragment}`));
  const primary = [...nodes.values()].find(node => primaryIds.has(node['@id']) && (node.url === canonical || node['@id'] === `${canonical}#items`));
  if (primary) {
    page.mainEntity = { '@id': primary['@id'] };
    primary.mainEntityOfPage = { '@id': page['@id'] };
  }
  const breadcrumb = nodes.get(`${canonical}#breadcrumb`);
  if (breadcrumb) page.breadcrumb = { '@id': breadcrumb['@id'] };
  nodes.set(page['@id'], page);
  return [...nodes.values(), ...anonymous];
}
